import '@/styles/globals.css'

import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'

import Head from 'next/head'
import { Inter, Archivo, Instrument_Serif } from 'next/font/google'

import { SmoothScroll } from '@/components/generals/smooth-scroll'
import { PageTransition } from '@/components/generals/page-transition'
import { Cursor } from '@/components/generals/cursor'

const sans = Inter({
    subsets: ['latin'],
    display: 'swap'
})

// Heavy grotesque for the wordmark and every large headline.
const display = Archivo({
    subsets: ['latin'],
    weight: ['500', '600', '700', '800'],
    display: 'swap'
})

// High-contrast italic serif, the counterpoint under the hero wordmark.
const script = Instrument_Serif({
    subsets: ['latin'],
    weight: '400',
    style: 'italic',
    display: 'swap'
})

// Declared at :root so overlays that portal to document.body keep the faces.
const fontVariables = `:root{--font-app-sans:${sans.style.fontFamily};--font-app-mono:ui-monospace,SFMono-Regular,Menlo,monospace;--font-app-heading:${display.style.fontFamily};--font-app-script:${script.style.fontFamily}}`

const App: FC<AppProps> = ({ Component, pageProps }): ReactNode => {
    return (
        <div className='h-full font-sans'>
            <Head>
                <meta name='viewport' content='width=device-width, initial-scale=1' />
                <style dangerouslySetInnerHTML={{ __html: fontVariables }} />
            </Head>

            <SmoothScroll>
                {/* Both mount once at app level so they survive route changes:
                    the cursor keeps its position and the transition overlay
                    needs to outlive the page it is covering. */}
                <Cursor />
                <PageTransition />

                <Component {...pageProps} />
            </SmoothScroll>
        </div>
    )
}

export default App
